import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  InputAdornment,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const API_BASE = "https://api.hmsaa.com.mx:5001";

const getToken = () =>
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") ||
  "";

const conceptosIniciales = [
  "transferencia",
  "reparacion",
  "instalacion",
  "diagnostico",
  "venta",
  "reemplazo",
  "reciclaje",
  "baja",
];

const descuentosDisponibles = [
  "0%", "5%", "10%", "15%", "20%", "25%", "30%", "35%", "40%", "45%", "50%",
  "55%", "60%", "65%", "70%", "75%", "80%", "85%", "90%", "95%", "100%",
];

async function apiFetch(path, options = {}) {
  const token = getToken();
  const url = `${API_BASE}${path}`;

  const headers = { ...(options.headers || {}) };

  // Mandar token SOLO si existe (ya no es requerido para productos)
  if (token) headers.Authorization = `Bearer ${token}`;

  if (options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} - ${txt}`);
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return res; // PDF (blob)
}

function parsePct(pctText) {
  const n = Number(String(pctText || "0").replace("%", ""));
  if (Number.isFinite(n)) return n / 100;
  return 0;
}

function toMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

const MovimientosPDF = () => {
  const [almacenes, setAlmacenes] = useState([]);
  const [almacenId, setAlmacenId] = useState("");

  const [productos, setProductos] = useState([]);
  const [personales, setPersonales] = useState([]);

  const [solicitante, setSolicitante] = useState({
    id_personal: "",
    nombre: "",
    departamento: "",
  });

  const [descuento, setDescuento] = useState("0%");
  const [iva, setIva] = useState(0.16);
  const [observaciones, setObservaciones] = useState("");

  const [concepto, setConcepto] = useState(
    conceptosIniciales.reduce((acc, c) => ({ ...acc, [c]: false }), {})
  );

  const [firmas, setFirmas] = useState({
    entrego: "",
    autorizo: "Bonifacio Salinas Salinas",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [verTodos, setVerTodos] = useState(false);

  // Almacenes
  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch("/api/almacenes", { method: "GET" });
        const arr = Array.isArray(data) ? data : (data.almacenes || []);
        setAlmacenes(arr);
      } catch (e) {
        console.error("almacenes:", e);
        alert(`Error cargando almacenes:\n${e.message}`);
        setAlmacenes([]);
      }
    })();
  }, []);

  // Personales
  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch("/api/personales", { method: "GET" });
        const arr = Array.isArray(data) ? data : (data.personales || []);

        const normalizados = arr.map((p) => ({
          id_personal: Number(p.id_personal ?? p.id ?? p.personal_id ?? 0),
          nombre: p.nombre ?? "",
          departamento: p.departamento ?? p.depto ?? "",
        }));

        setPersonales(normalizados);
      } catch (e) {
        console.error("personales:", e);
        alert(`Error cargando personales:\n${e.message}`);
        setPersonales([]);
      }
    })();
  }, []);

  // Productos por almacén (YA NO requiere JWT)
  useEffect(() => {
    (async () => {
      if (!almacenId) {
        setProductos([]);
        return;
      }

      try {
        const data = await apiFetch(`/api/productos/${almacenId}`, { method: "GET" });
        const arr = data?.productos || [];

        setProductos(
          arr.map((p) => ({
            ...p,
            selected: false,
            cantidad_edit: 1,
            precio_unitario_edit: p.precio_unitario ?? 0,
          }))
        );
      } catch (e) {
        console.error("productos:", e);
        alert(`Error cargando productos del almacén ${almacenId}:\n${e.message}`);
        setProductos([]);
      }
    })();
  }, [almacenId]);

  const handleSelectPersonal = (id_personal) => {
    const pid = Number(id_personal);

    const seleccionado = personales.find(
      (p) => Number(p.id_personal) === pid
    );

    if (!seleccionado || !pid) {
      setSolicitante({ id_personal: "", nombre: "", departamento: "" });
      return;
    }

    setSolicitante({
      id_personal: pid,
      nombre: seleccionado.nombre,
      departamento: seleccionado.departamento,
    });
  };

  const toggleProducto = (id_producto) => {
    setProductos((prev) =>
      prev.map((p) =>
        p.id_producto === id_producto ? { ...p, selected: !p.selected } : p
      )
    );
  };

  const actualizarProducto = (id_producto, campo, valor) => {
    setProductos((prev) =>
      prev.map((p) =>
        p.id_producto === id_producto ? { ...p, [campo]: valor } : p
      )
    );
  };

  const toggleConcepto = (key) => {
    setConcepto((prev) => {
      const nuevo = {};
      Object.keys(prev).forEach((k) => (nuevo[k] = false));
      nuevo[key] = true;
      return nuevo;
    });
  };

  const productosFiltrados = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return productos;
    return productos.filter((p) =>
      `${p.marca || ""} ${p.modelo || ""} ${p.descripcion || ""}`
        .toLowerCase()
        .includes(term)
    );
  }, [productos, searchTerm]);

  const productosAMostrar = verTodos
    ? productosFiltrados
    : productosFiltrados.slice(0, 5);

  const { subtotal, descuentoMonto, subtotalConDesc, ivaMonto, total } = useMemo(() => {
    const seleccionados = productos.filter((p) => p.selected);

    const sub = seleccionados.reduce((acc, p) => {
      const cant = Number(p.cantidad_edit || 0);
      const pu = Number(p.precio_unitario_edit || 0);
      return acc + cant * pu;
    }, 0);

    const pct = parsePct(descuento);
    const desc = sub * pct;
    const subDesc = Math.max(0, sub - desc);

    const ivaRate = Number(iva || 0);
    const ivaVal = subDesc * ivaRate;

    return {
      subtotal: sub,
      descuentoMonto: desc,
      subtotalConDesc: subDesc,
      ivaMonto: ivaVal,
      total: subDesc + ivaVal,
    };
  }, [productos, descuento, iva]);

  const handleGenerarPDF = async () => {
    const seleccionados = productos
      .filter((p) => p.selected)
      .map((p) => {
        const cantidad = Number(p.cantidad_edit || 0);
        const precioUnit = Number(p.precio_unitario_edit || 0);

        return {
          id_producto: p.id_producto,
          cantidad,
          descripcion: `${p.marca || ""} ${p.modelo || ""} - ${p.descripcion || ""}`.trim(),
          precio_unitario: precioUnit,
        };
      })
      .filter((p) => p.cantidad > 0);

    if (!almacenId) {
      alert("Selecciona un almacén");
      return;
    }
    const pid = Number(solicitante?.id_personal);

    if (!Number.isFinite(pid) || pid <= 0) {
      alert("Selecciona un personal válido");
      return;
    }

    if (seleccionados.length === 0) {
      alert("Selecciona al menos un producto y cantidad > 0");
      return;
    }

    const payload = {
      empresa: {
        nombre: "Hotel Malibu, S.A. de C.V.",
        direccion: "Av. Vallarta No. 3993, Zapopan, Jal.",
        contacto: "T: (33) 3889-5660, compras@hotelmalibu.com.mx",
        formato: "Movimientos en almacén de IT Forma: IT-03",
        formato2: "Documento no valido sin las firmas",
      },
      fecha: new Date().toLocaleDateString("es-MX"),
      solicitante: {
        id_personal: solicitante.id_personal,
        nombre: solicitante.nombre,
        departamento: solicitante.departamento,
      },
      solicitante: {
        id_personal: pid,
        nombre: solicitante.nombre,
        departamento: solicitante.departamento,
      },
      concepto,
      productos: seleccionados,
      observaciones,
      descuento_aplicado: descuento,
      iva: Number(iva || 0.16),
      firmas: {
        recibio: solicitante.nombre || "",
        entrego: firmas.entrego || "",
        autorizo: firmas.autorizo || "",
      },
    };

    try {
      const res = await apiFetch("/api/generar-pdf", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "movimiento_almacen.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("generar-pdf:", e);
      alert(`Error generando PDF:\n${String(e.message || e)}`);
    }
  };

  return (
    <Box sx={{ padding: 4, maxWidth: 900, margin: "auto" }}>
      <Typography variant="h4" gutterBottom>
        Movimientos en Almacén de TI
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="almacen-label">Selecciona Almacén</InputLabel>
        <Select
          labelId="almacen-label"
          label="Selecciona Almacén"
          value={almacenId}
          onChange={(e) => {
            setAlmacenId(e.target.value);
            setVerTodos(false);
            setSearchTerm("");
          }}
        >
          <MenuItem value="">--Seleccionar--</MenuItem>
          {almacenes.map((a) => (
            <MenuItem key={a.id_almacen} value={a.id_almacen}>
              {a.nombre_almacen}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="personal-label">Selecciona Personal</InputLabel>
        <Select
          labelId="personal-label"
          label="Selecciona Personal"
          value={solicitante.id_personal || ""}
          onChange={(e) => handleSelectPersonal(e.target.value)}
        >
          <MenuItem value="">--Seleccionar--</MenuItem>
          {personales.map((p) => (
            <MenuItem key={p.id_personal} value={p.id_personal}>
              {p.nombre} — {p.departamento}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField label="Nombre del solicitante" fullWidth value={solicitante.nombre} disabled />
        <TextField label="Departamento" fullWidth value={solicitante.departamento} disabled />
      </Box>

      <Typography variant="h6" gutterBottom>
        Conceptos del movimiento
      </Typography>
      <FormGroup row sx={{ mb: 3 }}>
        {conceptosIniciales.map((cKey) => (
          <FormControlLabel
            key={cKey}
            control={<Checkbox checked={!!concepto[cKey]} onChange={() => toggleConcepto(cKey)} />}
            label={cKey.charAt(0).toUpperCase() + cKey.slice(1)}
          />
        ))}
      </FormGroup>

      <TextField
        label="Buscar productos"
        variant="outlined"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Typography variant="h6" gutterBottom>
        Productos Disponibles {almacenId ? "" : "(elige almacén)"}
      </Typography>

      {productosAMostrar.map((p) => (
        <Box key={p.id_producto} sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={!!p.selected} onChange={() => toggleProducto(p.id_producto)} />}
            label={`${p.marca || ""} ${p.modelo || ""} - ${p.descripcion || ""}`}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 1, alignItems: "center" }}>
            <TextField
              label="Cantidad"
              type="number"
              inputProps={{ min: 1, max: Number(p.cantidad || 999999) }}
              value={p.cantidad_edit}
              onChange={(e) => actualizarProducto(p.id_producto, "cantidad_edit", e.target.value)}
              sx={{ width: 140 }}
              helperText={`Stock: ${p.cantidad ?? 0}`}
            />
            <TextField
              label="Precio unitario"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={p.precio_unitario_edit}
              onChange={(e) => actualizarProducto(p.id_producto, "precio_unitario_edit", e.target.value)}
              sx={{ width: 180 }}
            />
            <Typography sx={{ ml: "auto", fontWeight: 600 }}>
              Importe: {toMoney(Number(p.cantidad_edit || 0) * Number(p.precio_unitario_edit || 0))}
            </Typography>
          </Box>
        </Box>
      ))}

      {productosFiltrados.length > 5 && (
        <Button variant="text" onClick={() => setVerTodos(!verTodos)} sx={{ mb: 2 }}>
          {verTodos ? "Ver menos" : "Ver todos"}
        </Button>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="descuento-label">Descuento</InputLabel>
          <Select
            labelId="descuento-label"
            label="Descuento"
            value={descuento}
            onChange={(e) => setDescuento(e.target.value)}
          >
            {descuentosDisponibles.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="IVA"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={iva}
          onChange={(e) => setIva(e.target.value)}
          fullWidth
          helperText="0.16 = 16%"
        />
      </Box>

      <Box sx={{ border: "1px dashed #bbb", borderRadius: 2, p: 2, mb: 3 }}>
        <Typography>Subtotal: <b>{toMoney(subtotal)}</b></Typography>
        <Typography>Descuento ({descuento}): <b>-{toMoney(descuentoMonto)}</b></Typography>
        <Typography>Subtotal con descuento: <b>{toMoney(subtotalConDesc)}</b></Typography>
        <Typography>IVA ({Number(iva) * 100}%): <b>{toMoney(ivaMonto)}</b></Typography>
        <Typography variant="h6" sx={{ mt: 1 }}>
          Total: <b>{toMoney(total)}</b>
        </Typography>
      </Box>

      <Typography variant="h6" gutterBottom>
        Observaciones
      </Typography>
      <TextField
        multiline
        rows={4}
        fullWidth
        variant="outlined"
        value={observaciones}
        onChange={(e) => setObservaciones(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Firmas
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField label="Recibió" fullWidth value={solicitante.nombre} disabled />
        <TextField
          label="Entregó"
          fullWidth
          value={firmas.entrego}
          onChange={(e) => setFirmas((prev) => ({ ...prev, entrego: e.target.value }))}
        />
        <TextField
          label="Autorizó"
          fullWidth
          value={firmas.autorizo}
          onChange={(e) => setFirmas((prev) => ({ ...prev, autorizo: e.target.value }))}
        />
      </Box>

      <Button variant="contained" color="primary" onClick={handleGenerarPDF} size="large">
        📄 Generar PDF
      </Button>
    </Box>
  );
};

export default MovimientosPDF;
